import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const FAQ_ITEMS = ["refund", "payment", "transfer", "certificate", "account", "invoice"] as const;

interface ContactProducerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
  producerName: string;
  eventId?: string;
  eventTitle?: string;
  initialSubject?: string;
  initialDirectToForm?: boolean;
}

export function ContactProducerModal({
  open,
  onOpenChange,
  producerId,
  producerName,
  eventId,
  eventTitle,
  initialSubject,
  initialDirectToForm = false,
}: ContactProducerModalProps) {
  const { t } = useTranslation("eventDetail");
  const { user, profile } = useAuth();
  const [showForm, setShowForm] = useState(initialDirectToForm);

  // Set form visibility and prefill when modal opens
  useEffect(() => {
    if (open) {
      setShowForm(Boolean(initialDirectToForm));
      setFormData((prev) => ({
        ...prev,
        name: prev.name || profile?.full_name || "",
        email: prev.email || user?.email || "",
        subject: initialSubject || prev.subject || "",
      }));
    }
  }, [open, initialDirectToForm, initialSubject, profile?.full_name, user?.email]);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: initialSubject || "",
    message: "",
  });

  // Auto-fill from auth
  const handleShowForm = () => {
    if (!user) {
      toast({
        title: t("contactModal.toast.loginTitle"),
        description: t("contactModal.toast.loginDesc"),
        variant: "destructive",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      name: prev.name || profile?.full_name || "",
      email: prev.email || user?.email || "",
    }));
    setShowForm(true);
  };

  const handleSend = async () => {
    if (!user) {
      toast({ title: t("contactModal.toast.loginRequiredTitle"), description: t("contactModal.toast.loginRequiredDesc"), variant: "destructive" });
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      toast({ title: t("contactModal.toast.fillFields"), variant: "destructive" });
      return;
    }

    setSending(true);
    try {
      const payload = {
        producer_id: producerId,
        sender_id: user.id,
        sender_name: formData.name,
        sender_email: formData.email,
        event_id: eventId || null,
        subject: formData.subject,
        message: formData.message,
      };

      let { data: insertedMsg, error } = await supabase.from("producer_messages").insert(payload).select().single();
      if (error && /event_id/i.test(error.message || "")) {
        const { event_id: _ignored, ...legacyPayload } = payload;
        const legacyInsert = await supabase.from("producer_messages").insert(legacyPayload).select().single();
        error = legacyInsert.error;
        insertedMsg = legacyInsert.data;
      }

      if (error) throw error;
      
      // Trigger email notification for the producer
      if (insertedMsg) {
        await supabase.functions.invoke("send-message-notification", {
          body: { messageId: insertedMsg.id, type: "new_message" }
        }).catch(console.error);
      }
      toast({ title: t("contactModal.toast.sentTitle"), description: t("contactModal.toast.sentDesc", { name: producerName }) });
      setFormData({ name: "", email: "", subject: "", message: "" });
      setShowForm(false);
      onOpenChange(false);
    } catch (err) {
      toast({ title: t("contactModal.toast.errorTitle"), description: err.message || t("contactModal.toast.errorDesc"), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      setShowForm(false);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg rounded-3xl p-5 sm:p-7 max-h-[calc(100dvh-2rem)] overflow-y-auto [&_.dialog-close]:bg-muted [&_.dialog-close]:opacity-100">
        {!showForm ? (
          <>
            <DialogHeader className="mb-5 pr-10 text-left">
              <DialogTitle className="text-xl font-display font-semibold tracking-tight">{t("contactModal.faqTitle")}</DialogTitle>
              <p className="text-sm leading-relaxed text-muted-foreground mt-1">
                {t("contactModal.faqText")}{" "}
                <Link to="/faq" className="text-primary hover:underline font-medium" onClick={() => onOpenChange(false)}>
                  {t("contactModal.helpCenter")}
                </Link>
              </p>
            </DialogHeader>

            <Accordion type="single" collapsible className="mb-5 rounded-2xl border border-border/70 px-4">
              {FAQ_ITEMS.map((item) => (
                <AccordionItem key={item} value={item} className="border-border/60 last:border-b-0">
                  <AccordionTrigger className="min-h-12 gap-4 py-3.5 text-left text-sm font-medium leading-snug hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md motion-reduce:transition-none [&>svg]:text-muted-foreground [&>svg]:motion-reduce:transition-none">
                    {t(`contactModal.questions.${item}.question`)}
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 text-sm leading-relaxed text-muted-foreground">
                    {t(`contactModal.questions.${item}.answer`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="border-t border-border/60 pt-5 text-left space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("contactModal.promptText")}
              </p>
              <Button className="w-full h-auto min-h-12 rounded-xl whitespace-normal py-3" onClick={handleShowForm}>
                {user ? t("contactModal.talkButton") : t("contactModal.loginTalkButton")}
              </Button>
              {!user && (
                <p className="text-xs text-muted-foreground">
                  {t("contactModal.loginRequiredNote")}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="pr-10 text-left">
              <DialogTitle className="text-lg font-display">{t("contactModal.formTitle", { name: producerName })}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {t("contactModal.formSubtitle", { eventInfo: eventTitle ? ` sobre ${eventTitle}` : "" })}
              </p>
            </DialogHeader>

            <div className="space-y-4">
              {!user && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 flex items-center justify-between gap-2">
                  <span>Conecte-se para enviar sua mensagem ao organizador.</span>
                  <Button size="sm" variant="outline" asChild className="h-7 text-xs bg-white">
                    <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                      Entrar
                    </Link>
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="contact-name" className="text-xs">{t("contactModal.nameLabel")}</Label>
                  <Input
                    id="contact-name"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder={t("contactModal.namePlaceholder")}
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="text-xs">{t("contactModal.emailLabel")}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="email@exemplo.com"
                    disabled={!!user}
                    className={user ? "bg-muted" : ""}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-subject" className="text-xs">{t("contactModal.subjectLabel")}</Label>
                <Input
                  id="contact-subject"
                  value={formData.subject}
                  onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
                  placeholder={t("contactModal.subjectPlaceholder")}
                />
              </div>
              <div>
                <Label htmlFor="contact-message" className="text-xs">{t("contactModal.messageLabel")}</Label>
                <Textarea
                  id="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                  placeholder={t("contactModal.messagePlaceholder")}
                  rows={4}
                  maxLength={2000}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowForm(false)}>
                  {t("contactModal.backButton")}
                </Button>
                <Button className="flex-1 gap-2" onClick={handleSend} disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {t("contactModal.sendButton")}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
